import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../components/DropdownMenu';
import FormInput from '../../../../components/form/FormInput';
import { getValidationObject, validationTypes } from '../../../../constants/regexPatterns';
import { createErrorToast } from '../../../../redux/modules/app';
import {
  createAutomationStep,
  selectAutomationStep,
  selectCurrentAutomation,
  updateAutomationStep
} from '../../../../redux/modules/emailAutomations';
import { buildDefaultTemplatesForStep } from '../../../../redux/modules/emailAutomations/defaults';
import { emailEditorReset } from '../../../../redux/modules/emails';
import { hideModal } from '../../../../redux/modules/modal';
import { cleanCommaSeparatedEmailAddressString } from '../../../../utils/emailUtil';
import EmailBody from '../../../EmailEditorPage/components/EmailComponents/EmailBody';
import EmailSignature from '../../../EmailEditorPage/components/EmailComponents/EmailSignature';

const triggerTimeframeOptions = [
  { value: 'days_before', label: 'before' },
  { value: 'on_day_of', label: 'on' },
  { value: 'days_after', label: 'after' }
];

const CreateStepForm = ({ stepId }) => {
  const dispatch = useDispatch();
  const emailAutomation = useSelector(selectCurrentAutomation);
  const step = useSelector(selectAutomationStep(stepId));

  const notification = step?.notification;

  const useFormMethods = useForm({ defaultValues: { email: notification?.custom ?? null } });

  const { handleSubmit } = useFormMethods;

  const [emailBody, setEmailBody] = useState(step?.template?.body || '');
  const [emailSubject, setEmailSubject] = useState(step?.template?.subject || '');
  const [emailSignature, setEmailSignature] = useState(step?.template?.signature || '');
  const [notifyTeam, setNotifyTeam] = useState(!!notification?.id);
  const [dayValue, setDayValue] = useState(step?.timeframeValue ?? 1);
  const [customNotifyEmail, setCustomNotifyEmail] = useState(!!notification?.custom ?? null);
  const [departmentOwner, setDepartmentOwner] = useState(!!notification?.departmentOwner);
  const [projectOwner, setProjectOwner] = useState(!!notification?.projectOwner);
  const [recordWatchers, setRecordWatchers] = useState(!!notification?.recordWatchers);

  useEffect(() => {
    if (step) {
      setEmailBody(step?.template?.body ?? '');
      setEmailSubject(step?.template?.subject ?? '');
      setEmailSignature(step?.template?.signature ?? '');
    }
  }, [step?.template?.body, step?.template?.subject, step?.template?.signature]);

  const [triggerTimeframe, setTriggerTimeframe] = useState(
    triggerTimeframeOptions.find(option => option.value === step?.timeframeType)
    || triggerTimeframeOptions[0]
  );

  const defaultTemplates = useMemo(() => buildDefaultTemplatesForStep(
    emailAutomation.trigger.name,
    triggerTimeframe.value,
    dayValue
  ), [
    triggerTimeframe.value,
    dayValue
  ]);

  const buildName = () => {
    if (triggerTimeframe.value === 'on_day_of') {
      return 'On day of expiration';
    }

    return `${dayValue} days ${triggerTimeframe.label} expiration`;
  };

  const onSave = async formData => {
    const { email: emailValue } = formData;

    if (!emailBody || !emailSubject) {
      dispatch(createErrorToast({
        title: 'Subject and/or Body Required',
        description: 'Please provide a subject and body for the email template.'
      }));

      return;
    }

    const data = {
      name: buildName(),
      timeframe_value: dayValue,
      timeframe_type: triggerTimeframe.value,
      template: {
        body: emailBody,
        subject: emailSubject,
        signature: emailSignature
      }
    };

    if (notifyTeam) {
      data.notification_settings = {
        department_owner: departmentOwner,
        project_owner: projectOwner,
        record_watchers: recordWatchers
      };

      if (customNotifyEmail && emailValue) {
        data.notification_settings.custom = cleanCommaSeparatedEmailAddressString(emailValue);
      }
    }

    if (step) {
      dispatch(updateAutomationStep(step.id, data));
      dispatch(emailEditorReset());
      dispatch(hideModal());
      return;
    }

    dispatch(createAutomationStep(emailAutomation.id, data));
    dispatch(emailEditorReset());
    dispatch(hideModal());
  };

  return (
    <VStack pt={4} spacing={6}>
      <Text color="greyScale.900" textStyle="displayXxs600">{step ? 'Edit Step' : 'Create a new step'}</Text>
      <Flex alignItems="center" mt={8}>
        <Text mr={2} textStyle="bodyMd600">Send emails</Text>
        {triggerTimeframe.value !== 'on_day_of' && (
          <>
            <NumberInput
              defaultValue={dayValue}
              max={180}
              maxWidth="80px"
              min={1}
              size="md"
              onChange={val => setDayValue(val)}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text mx={2} textStyle="bodyMd600">days</Text>
          </>
        )}
        <DropdownMenu
          useOriginal
          options={triggerTimeframeOptions}
          selected={triggerTimeframe}
          width="100px"
          onSelect={setTriggerTimeframe}
        />
        <Text ml={2} textStyle="bodyMd600">expiration date</Text>
      </Flex>
      <Box mt={8}>
        <HStack>
          <Checkbox isChecked={notifyTeam} mr={4} onChange={() => setNotifyTeam(!notifyTeam)} />
          <Text textStyle="bodyMd600">Notify your team when this step is reached</Text>
        </HStack>

        {notifyTeam && (
          <Box>
            <HStack alignItems="flex-start" justifyContent="space-between" mt={8} textAlign="left">
              <Text mr={8} textStyle="bodyMd500">Who should we notify?</Text>
              <Box>
                <Box mb={2}>
                  <Checkbox
                    isChecked={departmentOwner}
                    onChange={() => setDepartmentOwner(!departmentOwner)}
                  >
                    Department Owners
                  </Checkbox>
                </Box>
                <Box mb={2}>
                  <Checkbox
                    isChecked={projectOwner}
                    onChange={() => setProjectOwner(!projectOwner)}
                  >
                    Project Owners
                  </Checkbox>
                </Box>
                <Box mb={2}>
                  <Checkbox
                    isChecked={recordWatchers}
                    onChange={() => setRecordWatchers(!recordWatchers)}
                  >
                    Record Watchers
                  </Checkbox>
                </Box>
                <Box mb={2}>
                  <Checkbox
                    isChecked={customNotifyEmail}
                    mb={2}
                    onChange={() => setCustomNotifyEmail(!customNotifyEmail)}
                  >
                    Custom
                  </Checkbox>
                </Box>
              </Box>
            </HStack>
            {customNotifyEmail && (
              <Box width="75%">
                <form>
                  <FormInput
                    disableEnterKey
                    inputName="email"
                    labelName="Custom Email"
                    placeholder="Enter comma separated email addresses"
                    required={false}
                    useFormMethods={useFormMethods}
                    validationRules={getValidationObject(validationTypes.commaSeparatedEmailAddress)}
                  />
                </form>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider borderBottomWidth="3px" />
      <VStack alignItems="stretch" maxH="500px" mt={8} overflowY="auto" spacing={4}>
        <EmailBody
          disableTemplateCreation
          automationTrigger={emailAutomation.trigger.name}
          backgroundColor="greyScale.100"
          bodyContainerId="automation-body"
          bodyValue={emailBody}
          defaultTemplates={defaultTemplates}
          selectCodeType="popover"
          subjectContainerId="automation-subject"
          subjectValue={emailSubject}
          onBodyChange={setEmailBody}
          onSubjectChange={setEmailSubject}
        />
        <EmailSignature
          disableTemplateCreation
          automationTrigger={emailAutomation.trigger.name}
          backgroundColor="greyScale.100"
          signatureContainerId="automation-signature"
          value={emailSignature}
          onChange={setEmailSignature}
        />
      </VStack>
      <HStack flexFlow="row nowrap" justifyContent="center" mt={6} spacing={4}>
        <Button minW="100px" variant="secondary" onClick={() => dispatch(hideModal())}>Cancel</Button>
        <Button minW="100px" variant="primary" onClick={handleSubmit(onSave)}>Save</Button>
      </HStack>
    </VStack>
  );
};

CreateStepForm.propTypes = { stepId: PropTypes.number };

export default CreateStepForm;
